import type { ReactNode } from 'react'
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

interface ItemId { id: string }

function SortableRow({ id, dark, children }: { id: string; dark: boolean; children: ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 }
  const grip = dark ? 'text-slate-500 hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'
  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-2">
      <button
        type="button"
        className={`shrink-0 cursor-grab active:cursor-grabbing touch-none px-1 ${grip}`}
        aria-label="Arrastrar para reordenar"
        {...attributes}
        {...listeners}
      >
        <svg className="w-4 h-4" viewBox="0 0 16 16" fill="currentColor"><circle cx="5" cy="3" r="1.3"/><circle cx="11" cy="3" r="1.3"/><circle cx="5" cy="8" r="1.3"/><circle cx="11" cy="8" r="1.3"/><circle cx="5" cy="13" r="1.3"/><circle cx="11" cy="13" r="1.3"/></svg>
      </button>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  )
}

interface SortableListProps<T extends ItemId> {
  items: T[]
  dark: boolean
  onReorder: (ordered: T[]) => void
  children: (item: T) => ReactNode
}

/** Lista reordenable por drag & drop (handle con grip). Reusa @dnd-kit. */
export function SortableList<T extends ItemId>({ items, dark, onReorder, children }: SortableListProps<T>) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  function onDragEnd(e: DragEndEvent) {
    const { active, over } = e
    if (over && active.id !== over.id) {
      const from = items.findIndex((i) => i.id === active.id)
      const to = items.findIndex((i) => i.id === over.id)
      if (from !== -1 && to !== -1) onReorder(arrayMove(items, from, to))
    }
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-2">
          {items.map((it) => (
            <SortableRow key={it.id} id={it.id} dark={dark}>{children(it)}</SortableRow>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  )
}
